import styled, { css, keyframes, createGlobalStyle } from "styled-components";

import { ActionDescriptionProps, SkeletonBoxProps } from "./types";
import { Link } from "wouter";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../utils/constants";
import { BaseButton } from "../../components/Button";

// Enhanced focus styles for accessibility
export const AccessibilityStyles = createGlobalStyle`
  /* Enhanced focus indicators for better keyboard navigation */
  *:focus-visible {
    outline: 2px solid var(--color-primary-color-background);
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* Skip to main content link for screen readers */
  .skip-to-content {
    position: absolute;
    left: -9999px;
    z-index: 999;
    padding: ${SPACING.md}px;
    background-color: var(--color-primary-color-background);
    color: var(--color-white);
    text-decoration: none;
    border-radius: 4px;
  }

  .skip-to-content:focus {
    left: 50%;
    transform: translateX(-50%);
    top: 1rem;
  }

  /* Better focus for buttons */
  button:focus-visible {
    outline: 2px solid var(--color-primary-color-background);
    outline-offset: 2px;
  }

  /* Better focus for inputs */
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: 2px solid var(--color-primary-color-background);
    outline-offset: 0;
    border-color: var(--color-primary-color-background);
  }
`;

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  min-height: 0%;
  gap: 0;
  padding: 0;
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
  grid-template-areas:
    "header"
    "content";

  /* Center the whole page (header + content) as one fixed-width column so the
     nav sits near the center and the title aligns with the nav menu. */
  @media ${QUERIES.tabletLargeAndUp} {
    width: 100%;
    max-width: 73.75rem;
    margin-inline: auto;
  }
`;

export const Content = styled.div`
  grid-area: content;
  flex: 1;
  margin: 0 ${SPACING_RESPONSIVE.mobile.chartMargin}px
    ${SPACING_RESPONSIVE.mobile.sectionGap}px;
  overflow: auto;

  @media ${QUERIES.tabletAndUp} {
    margin: 0 ${SPACING_RESPONSIVE.tablet.chartMargin}px
      ${SPACING_RESPONSIVE.tablet.sectionGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: 0 ${SPACING_RESPONSIVE.desktop.chartMargin}px
      ${SPACING_RESPONSIVE.desktop.sectionGap}px;
  }

  /* On the two-column layout, match the header's 2rem padding so the sidebar's
     left edge lines up with the page title. */
  @media ${QUERIES.tabletLargeAndUp} {
    margin-left: ${SPACING.xl}px;
    margin-right: ${SPACING.xl}px;
  }
`;

export const Card = styled.div`
  color: var(--color-text-default);
  background: var(--color-card-background);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-small-2);
  border: 1px solid var(--color-card-border);
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  margin-bottom: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
    margin-bottom: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
    margin-bottom: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
    border-radius: 0.75rem;
  }

  /* Mobile profile/security view: drop the card chrome so fields sit flat on
     the page, matching the menu-first detail design. Keep a small horizontal
     inset so the inputs' focus ring isn't clipped by the Content scroll
     container's edge. */
  &.profile,
  &.security,
  &.preferences,
  &.goals,
  &.reports,
  &.account,
  &.contact {
    @media ${QUERIES.tabletLargeAndDown} {
      background: transparent;
      border: none;
      box-shadow: none;
      padding: 0 ${SPACING.sm}px;
    }
  }
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  margin-bottom: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    margin-bottom: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin-bottom: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xs}px;
`;

export const IconContainer = styled.div<ActionDescriptionProps>`
  width: 2.75rem;
  height: 2.75rem;
  min-width: 2rem;
  min-height: 2rem;
  flex-shrink: 0;
  background-color: var(--color-info-background);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);

  svg {
    font-size: var(--text-xl);
  }

  &.info {
    background-color: var(--color-info-background);
    color: var(--color-info);
  }

  &.warning {
    background-color: var(--color-warning-background);
    color: var(--color-warning);
  }

  &.danger {
    background: var(--color-error-background);
    color: var(--color-error);
  }

  @media ${QUERIES.tabletAndDown} {
    width: 2.5rem;
    height: 2.5rem;
    min-width: 2.5rem;
    min-height: 2.5rem;

    svg {
      font-size: var(--text-xxl);
    }
  }
`;

export const CardTitle = styled.h2`
  font-size: var(--text-lg);
  font-weight: 600;
  margin: 0;
  color: var(--color-text-default);

  @media ${QUERIES.tabletAndDown} {
    font-size: var(--text-xl);
  }
`;

export const CardDescription = styled.p`
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;

  @media ${QUERIES.tabletAndDown} {
    font-size: var(--text-base);
    line-height: 1.6;
  }

  /* Quieter, italic intro on the flat mobile profile/security view. */
  @media ${QUERIES.tabletLargeAndDown} {
    .profile &,
    .security & {
      font-style: italic;
      color: var(--color-text-secondary);
    }
  }

  /* Preferences and contact keep the quiet italic intro at every breakpoint. */
  .preferences &,
  .contact & {
    font-style: italic;
  }
`;

export const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;
  margin-bottom: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    margin-bottom: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin-bottom: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
  }

  @media ${QUERIES.tabletLargeAndDown} {
    gap: ${SPACING.md}px;
  }
`;

export const AvatarContainer = styled.div`
  position: relative;
`;

export const Avatar = styled.div`
  width: 3rem;
  height: 3rem;
  background: var(--color-primary-color-background);
  color: var(--color-white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-xxl);
  font-weight: 600;

  @media ${QUERIES.tabletAndDown} {
    width: 4rem;
    height: 4rem;
    font-size: var(--text-xl);
  }
`;

export const ProfileInfo = styled.div`
  @media ${QUERIES.tabletLargeAndDown} {
    display: flex;
    flex-direction: column;
    gap: ${SPACING.xs + SPACING.xxs}px;
    min-width: 0;
  }
`;

export const ProfileName = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  margin-bottom: ${SPACING.xs}px;

  @media ${QUERIES.tabletLargeAndDown} {
    font-size: var(--text-xl);
    margin-bottom: 0;
  }
`;

export const Badge = styled.span`
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  font-size: var(--text-xs);
  background-color: var(--color-success-background-2);
  color: var(--color-success-6);
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs + SPACING.xxs}px;
  align-self: flex-start;
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xxs}px;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid var(--color-success-border);

  .dot {
    display: inline-block;
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    background-color: var(--color-success);
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;
  margin: ${SPACING_RESPONSIVE.mobile.chartPadding}px 0;

  .full-width {
    grid-column: 1 / -1;
  }

  @media ${QUERIES.tabletAndUp} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
    margin: ${SPACING_RESPONSIVE.tablet.chartPadding}px 0;
  }

  @media ${QUERIES.laptopAndUp} {
    gap: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
    margin: ${SPACING_RESPONSIVE.desktop.chartPadding}px 0;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const Label = styled.label`
  display: block;
  font-size: var(--text-base);
  font-weight: 500;
  margin-bottom: ${SPACING.xs}px;
  color: var(--color-text-default);

  .required {
    color: var(--color-error);
    margin-left: ${SPACING.xxs}px;
  }

  .field-tag {
    display: none;
  }
`;

export const HelperText = styled.span`
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  margin-top: ${SPACING.xs}px;
  line-height: 1.4;
  display: block;

  @media ${QUERIES.tabletLargeAndDown} {
    .profile &,
    .security & {
      font-style: italic;
    }
  }
`;

export const SectionTitle = styled.h3`
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--color-text-default);
  margin: 0 0 ${SPACING.sm + SPACING.xs}px 0;
`;

export const SectionDescription = styled.p`
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  margin: 0 0 ${SPACING.md}px 0;
  line-height: 1.5;
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--color-border);
  margin: ${SPACING_RESPONSIVE.mobile.cardGridGap}px 0;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.cardGridGap}px 0;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.cardGridGap}px 0;
  }

  /* The flat mobile profile separates sections with whitespace, not a rule. */
  @media ${QUERIES.tabletLargeAndDown} {
    .profile & {
      display: none;
    }
  }
`;

/* A settings row that pairs a text block with a control (e.g. the language
   picker), mirroring the Toggle component's label-left / control-right rhythm. */
export const PreferenceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.sm + SPACING.xs}px 0;

  @media ${QUERIES.tabletAndDown} {
    padding: ${SPACING.md}px 0;
  }
`;

export const PreferenceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PreferenceLabel = styled.div`
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-text-default);

  @media ${QUERIES.tabletAndDown} {
    font-size: var(--text-md);
  }
`;

export const PreferenceDescription = styled.div`
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  line-height: 1.4;
  margin-top: ${SPACING.xxs}px;
`;

export const PreferenceControl = styled.div`
  flex-shrink: 0;
  width: 12rem;

  @media ${QUERIES.tabletAndDown} {
    width: 9rem;
  }
`;

/* ----- Account management ----- */

/* A labelled block (statistics, actions) inside the account card. Sections are
   separated by generous whitespace rather than rules. */
export const AccountSection = styled.section`
  & + & {
    margin-top: ${SPACING.xl}px;
  }
`;

/* Small uppercase eyebrow that titles each account section. */
export const SectionEyebrow = styled.h3`
  margin: 0 0 ${SPACING.md}px 0;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

/* Two columns on mobile, four across on the desktop card. */
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    gap: ${SPACING.md}px;
  }

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: repeat(4, 1fr);
    gap: ${SPACING.md}px;
  }
`;

export const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.md}px;
  background: var(--color-background-2);
  border: 1px solid var(--color-card-border);
  border-radius: 0.625rem;

  /* On the flat mobile layout the surrounding card chrome is gone, so the stat
     tiles become the only solid surface against the page background. */
  @media ${QUERIES.tabletLargeAndDown} {
    background: var(--color-card-background);
  }
`;

export const StatLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

/* The stat figure: bold and a touch larger than its uppercase label. */
export const StatValue = styled.span`
  font-size: var(--text-md);
  font-weight: 600;
  line-height: 1.2;
  color: var(--color-text-default);
`;

/* Stacked rows of account actions. */
export const ActionList = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Each action stacks its copy above a full-width button on mobile, and on the
   desktop card lays the copy and button side by side, separated by a rule. */
export const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING.lg}px 0;

  &:first-of-type {
    padding-top: 0;
  }

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACING.xl}px;

    & + & {
      border-top: 1px solid var(--color-border);
    }
  }
`;

export const ActionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 0;

  @media ${QUERIES.tabletAndUp} {
    max-width: 60ch;
  }
`;

export const ActionRowTitle = styled.h4`
  margin: 0;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--color-text-default);
`;

export const ActionRowDescription = styled.p`
  margin: 0;
  font-size: var(--text-base);
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

/* Holds a single action button: full width on mobile, intrinsic width beside
   the copy on desktop. */
export const ActionControl = styled.div`
  flex-shrink: 0;
  width: 100%;

  button {
    width: 100%;
    min-height: 44px;
  }

  @media ${QUERIES.tabletAndUp} {
    width: auto;

    button {
      width: auto;
      min-height: revert;
    }
  }
`;

/* The destructive action sits apart in a tinted, bordered danger panel. */
export const DangerCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin-top: ${SPACING.lg}px;
  padding: ${SPACING.lg}px;
  background: var(--color-error-background);
  border: 1px solid var(--color-error-border);
  border-radius: 0.625rem;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACING.xl}px;
  }
`;

export const DangerTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  color: var(--color-error);

  svg {
    font-size: var(--text-xl);
  }
`;

export const SecurityContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACING.md}px;
`;

export const SecurityDescription = styled.p`
  font-size: var(--text-base);
  margin-bottom: ${SPACING.sm}px;
`;

export const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    gap: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    gap: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
  }
`;

// FAQ Section Styles
export const FAQSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm + SPACING.xs}px;
`;

export const FAQItem = styled.div`
  border: 1px solid var(--color-card-border);
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--color-card-background);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: var(--shadow-small-1);
  }

  /* On the flat mobile layout the surrounding card chrome is gone, so each item
     becomes a standalone raised pill against the page background. */
  @media ${QUERIES.tabletLargeAndDown} {
    border-radius: 0.75rem;
    box-shadow: var(--shadow-small-1);
  }
`;

export const FAQQuestion = styled(BaseButton)<{ $isOpen: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  font-size: var(--text-base);
  font-weight: 500;
  text-align: left;
  color: var(--color-text-default);
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  /* Inset ring: the accordion card clips an outer one. */
  &:focus-visible {
    outline: 2px solid var(--color-primary-color-background);
    outline-offset: -2px;
    box-shadow: none;
  }

  svg {
    flex-shrink: 0;
    font-size: var(--text-xl);
    color: var(--color-text-secondary);
    transition: transform 0.2s ease;
    transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0)")};
  }

  @media ${QUERIES.tabletAndDown} {
    font-size: var(--text-md);
    padding: ${SPACING.md + SPACING.xxs}px;
  }
`;

export const FAQAnswer = styled.div<{ $isOpen: boolean }>`
  max-height: ${(props) => (props.$isOpen ? "500px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

export const FAQAnswerContent = styled.div`
  padding: 0 ${SPACING.md}px ${SPACING.md}px;
  font-size: var(--text-base);
  line-height: 1.6;
  color: var(--color-text-secondary);

  a {
    color: var(--color-primary-color-background);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }

  @media ${QUERIES.tabletAndDown} {
    font-size: var(--text-base);
    padding: 0 ${SPACING.md + SPACING.xxs}px ${SPACING.md + SPACING.xxs}px;
  }
`;

// Contact form layout
export const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;
`;

export const ContactField = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FieldError = styled.span`
  margin-top: ${SPACING.xs}px;
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--color-error);
`;

/* The submit button sits left-aligned at its intrinsic width on every screen. */
export const ContactActions = styled.div`
  display: flex;
  margin-top: -${SPACING.lg}px;
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.lg}px;
  align-items: start;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: minmax(13.75rem, 16.25rem) 1fr;
  }
`;

export const Sidebar = styled.aside`
  position: sticky;
  top: ${SPACING.md}px;
`;

export const Panel = styled.div`
  min-width: 0;
  width: 100%;
`;

/* ----- Loading skeleton -----
   Mirrors the loaded composition (nav list on mobile, sidebar + panel on
   desktop) so the page holds its shape while user details load, instead of
   collapsing to flat blocks that jump when the real layout resolves. */

const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const skeletonSurface = css`
  background: var(--gradient-skeleton);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ${ANIMATION.easing.easeInOut} infinite;
`;

export const SkeletonBox = styled.div<SkeletonBoxProps>`
  ${skeletonSurface}
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "1rem"};
  border-radius: 4px;
`;

/* Icon placeholder mirrors the nav/section icon: 2.75rem on mobile rows,
   compact 2rem in the desktop sidebar. */
export const SkeletonIcon = styled.div`
  ${skeletonSurface}
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.5rem;

  @media ${QUERIES.tabletLargeAndUp} {
    width: 2rem;
    height: 2rem;
  }
`;

export const SkeletonNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    gap: ${SPACING.xs}px;
  }
`;

/* Mirrors NavRow: a raised card on mobile, transparent and compact in the
   desktop sidebar. */
export const SkeletonNavRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  border-radius: 0.75rem;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  box-shadow: var(--shadow-small-1);

  @media ${QUERIES.tabletLargeAndUp} {
    gap: ${SPACING.sm}px;
    padding: ${SPACING.sm}px ${SPACING.md}px;
    background: transparent;
    border-color: transparent;
    box-shadow: none;
  }
`;

export const SkeletonTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs + SPACING.xxs}px;
  flex: 1;
  min-width: 0;
`;

/* Mirrors the section Card: chrome on desktop, flat on the mobile detail view. */
export const SkeletonCard = styled.div`
  background: var(--color-card-background);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-small-2);
  border: 1px solid var(--color-card-border);
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
  }

  @media ${QUERIES.tabletLargeAndDown} {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0 ${SPACING.sm}px;
  }
`;

export const SkeletonCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xs}px;
  margin-bottom: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    margin-bottom: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin-bottom: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
  }
`;

export const SkeletonFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletAndUp} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    gap: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
  }
`;

export const SkeletonField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const SkeletonBackLink = styled.div`
  margin: ${SPACING.md}px 0;
`;

export const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  margin-bottom: ${SPACING.md}px;
  font-size: var(--text-base);
  font-weight: 500;
  color: var(--color-primary);
  margin-top: ${SPACING.md}px;
  text-decoration: none;

  svg {
    font-size: var(--text-xl);
  }

  &:hover {
    text-decoration: underline;
  }
`;
